import { useState } from "react";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import LegalAcceptanceModal from "./LegalAcceptanceModal";

/**
 * LegalReacceptGate: blocks the app when the signed-in user is missing
 * the current legal-bundle acceptance.
 *
 * This covers three scenarios:
 *   1. Existing user, legal bundle was bumped → they must re-accept.
 *   2. Staff member account created by the owner (no acceptance record
 *      on file) → first-time forced acceptance covers Terms + Privacy +
 *      Acceptable Use.
 *   3. Customer or owner with stale acceptance after policy update.
 *
 * The gate is not cancelable. It writes back via POST /account/accept-legal
 * and refreshes the auth context so `requires_legal_reaccept` flips false.
 */
export default function LegalReacceptGate() {
  const { user, company, refresh, logout } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user || !user.requires_legal_reaccept) return null;

  const role = user.account_type || user.role;
  const isOwner = role === "business_owner";
  const isStaff = !!company && !isOwner && role !== "customer" && role !== "platform_admin";
  const mode = isOwner ? "business" : isStaff ? "staff" : "customer";

  const accept = async ({ legal_bundle_version }) => {
    setBusy(true);
    try {
      await api.post("/account/accept-legal", {
        legal_bundle_version,
        accept_business_terms: isOwner,
      });
      toast.success("Thanks: your acceptance has been recorded.");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <LegalAcceptanceModal
      open={true}
      cancelable={false}
      onAccept={accept}
      onClose={() => { /* not cancelable */ }}
      mode={mode}
      title="Updated legal terms: please review and accept"
      subtitle={
        isStaff
          ? "Before you can use ConfirmPH on behalf of your business, please read and accept the Terms, Privacy Policy and Acceptable Use Policy."
          : "Our legal terms have been updated. Please read the summary below and confirm before continuing."
      }
      acceptCta={busy ? "Saving…" : "I agree: continue"}
    />
  );
}
